import React, { useState, useEffect } from 'react';
import { Camera, Send, Sparkles, CheckCircle, PartyPopper } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';
import { supabase } from '../supabaseClient';

const AVAILABLE_SERVICES = [
  'AI Photo Booth',
  '360 Photo Booth',
  'Roaming Photo Booth',
  'Pro Photography',
  'Photo Printing',
  'Glamour Booth',
  'Digital Kiosk'
];

export const QuickInquiry = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [referrer, setReferrer] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '', // ⚡ NEW: Added Company to state
    eventDate: ''
  });
  
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  // Grab the referral name from the URL automatically!
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferrer(ref.replace('_', ' '));
  }, []);

  const toggleService = (service: string) => {
    setSelectedServices(prev => 
      prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.email) return alert("First name and email are required!");
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase.rpc('submit_quick_enquiry', {p_fields: {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company_name: formData.company || null, // ⚡ NEW: Sends to the database
        event_date: formData.eventDate || null,
        services_interested: selectedServices.length > 0 ? selectedServices.join(', ') : null,
        lead_source: referrer ? `Crew Referral: ${referrer}` : 'Quick Inquiry Link',
        status: 'INQUIRY',
        project_ref: `Quick Inquiry: ${formData.firstName} ${formData.lastName}`
      }});

      if (error) throw error;
      setIsSubmitted(true);
    } catch (err: any) {
      alert("Something went wrong. Please try again or find our crew member!");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <GlassCard className="w-full max-w-md p-10 text-center border-brand-neonBlue/30 shadow-neon">
          <div className="w-20 h-20 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner border border-green-500/50">
            <PartyPopper size={40} />
          </div>
          <h2 className="text-3xl font-display font-bold text-white tracking-tight mb-4">Got It!</h2>
          <p className="text-slate-400 mb-8 font-medium">
            Thanks for reaching out! Our team will review your details and send you a custom quote shortly.
          </p>
          <Button onClick={() => window.location.href = 'https://quikpix.co.nz'} className="w-full bg-brand-neonBlue text-slate-950 font-bold rounded-2xl py-4 shadow-neon">
            Back to QuikPix
          </Button>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-8 pb-24 overflow-y-auto">
      <div className="w-full max-w-lg mx-auto">
        
        {/* Dynamic Header */}
        <div className="text-center mb-8 pt-4">
          <div className="w-16 h-16 bg-brand-neonBlue/10 text-brand-neonBlue rounded-full flex items-center justify-center mx-auto mb-4 border border-brand-neonBlue/30">
            <Camera size={32} />
          </div>
          <h1 className="text-3xl font-display font-black text-white tracking-tight">Let's Create Magic</h1>
          {referrer && (
            <p className="text-brand-neonPink text-sm font-bold uppercase tracking-widest mt-2 flex items-center justify-center gap-2">
              <Sparkles size={14}/> Referred by {referrer}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <GlassCard className="p-6 md:p-8 space-y-6 border-white/5 bg-slate-900/40 rounded-[32px]">
            
            {/* The Tap-Friendly Service Selection */}
            <div>
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-4 text-center">What are you looking for?</label>
              <div className="flex flex-wrap justify-center gap-2">
                {AVAILABLE_SERVICES.map(service => {
                  const isSelected = selectedServices.includes(service);
                  return (
                    <button
                      key={service}
                      type="button"
                      onClick={() => toggleService(service)}
                      className={`px-4 py-3 rounded-full text-xs font-bold transition-all duration-300 border ${
                        isSelected 
                          ? 'bg-brand-neonBlue text-slate-950 border-brand-neonBlue shadow-neon-blue-sm scale-105' 
                          : 'bg-slate-950 text-slate-400 border-white/10 hover:border-brand-neonBlue/50'
                      }`}
                    >
                      {isSelected && <CheckCircle size={14} className="inline mr-1.5 -mt-0.5" />}
                      {service}
                    </button>
                  );
                })}
              </div>
            </div>

            <hr className="border-white/5" />

            {/* Core Details */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input required className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="First Name *" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} />
                <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="Last Name" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} />
              </div>
              
              <input required type="email" className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="Email Address *" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              
              {/* ⚡ NEW: Company Field */}
              <input className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="Company / Organization (Optional)" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} />
              
              <input type="tel" className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors" placeholder="Mobile Number" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
              
              <div className="space-y-2 pt-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2">Event Date (If known)</label>
                <input type="date" className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors [color-scheme:dark]" value={formData.eventDate} onChange={e => setFormData({...formData, eventDate: e.target.value})} />
              </div>
            </div>

          </GlassCard>

          <Button type="submit" disabled={isLoading} className="w-full py-5 text-lg bg-brand-neonBlue text-slate-950 font-bold rounded-[24px] shadow-neon border-none hover:scale-[1.02] transition-transform">
            {isLoading ? 'Sending...' : 'Send Inquiry'} <Send size={18} className="ml-2" />
          </Button>
        </form>

      </div>
    </div>
  );
};